'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, TrendingUp, Shield, Award, Zap } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { isAdminRole, isClientRole } from '@/lib/supabaseService';

interface LoginForm {
  email: string;
  password: string;
}

export default function LoginPage() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<LoginForm>();

  // The website forwards ?email= when it could not hand over a session (for
  // example when email confirmation is still required).
  useEffect(() => {
    const email = new URLSearchParams(window.location.search).get('email');
    if (email) setValue('email', email);
  }, [setValue]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) return;
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('role, is_suspended')
        .eq('id', session.user.id)
        .single();
      if (error || !profile || profile.is_suspended) return;
      if (isAdminRole(profile.role)) router.replace('/admin-panel');
      else if (isClientRole(profile.role)) router.replace('/client-dashboard');
    });
  }, []);

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error || !authData.user) {
      setError('root', {
        message: 'Invalid email or password. Please check your credentials.',
      });
      setIsLoading(false);
      return;
    }

    // Fetch profile to check role and suspension (support both full_name and name)
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('role, is_suspended, full_name, name')
      .eq('id', authData.user.id)
      .single();

    if (profileError || !profile) {
      setError('root', {
        message: `Unable to load your profile${profileError?.message ? `: ${profileError.message}` : ''}. Please try again or contact support.`,
      });
      setIsLoading(false);
      return;
    }

    if (profile.is_suspended) {
      await supabase.auth.signOut();
      setError('root', {
        message: 'Your account is currently under review due to regulatory standards — please contact support at support@unitedfxa.com.',
      });
      setIsLoading(false);
      return;
    }

    const displayName =
      profile.full_name ||
      profile.name ||
      authData.user.user_metadata?.full_name ||
      authData.user.user_metadata?.name ||
      authData.user.email ||
      'there';
    const firstName = String(displayName).split(' ')[0] || 'there';
    toast.success(`Welcome back, ${firstName}!`);

    const dest = isAdminRole(profile.role) ? '/admin-panel' : '/client-dashboard';
    // Full navigation so session storage is definitely available on the next page
    window.location.assign(dest);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: '#000000' }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
          <div className="absolute bottom-40 left-10 w-96 h-96 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5"
            style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
        </div>

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
            <span className="text-black font-bold text-sm">FX</span>
          </div>
          <div>
            <span className="text-white font-bold text-xl tracking-tight">United</span>
            <span className="text-gradient-gold font-bold text-xl tracking-tight">FXA</span>
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.4)' }}>
              <Zap size={12} />
              Award-Winning Forex Broker
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Navigate Markets<br />
              <span className="text-gradient-gold">with Confidence</span>
            </h1>
            <p className="text-lg max-w-md leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Access your UnitedFXA trading account, manage deposits, withdrawals, and track your performance in real time.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: '2,600+', label: 'Instruments' },
              { value: '1:400', label: 'Max Leverage' },
              { value: '84.65%', label: 'Win Ratio' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="text-xl font-bold font-tabular text-gradient-gold">{stat.value}</div>
                <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Feature list */}
          <div className="space-y-3">
            {[
              { icon: TrendingUp, text: 'MT5 platform with 2,600+ trading instruments' },
              { icon: Shield, text: 'Phase 1, Phase 2, and Funded account programs' },
              { icon: Award, text: 'Global Forex Awards 2018, 2021 winner' },
            ].map((item) => (
              <div key={`feat-${item.text.slice(0, 20)}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(245,158,11,0.15)' }}>
                  <item.icon size={14} style={{ color: '#F59E0B' }} />
                </div>
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
          © 2026 UnitedFXA. All rights reserved. Trading involves significant risk of loss.{' '}
          <a href="https://www.unitedfxa.com" target="_blank" rel="noopener noreferrer" style={{ color: '#F59E0B' }} className="hover:underline">
            www.unitedfxa.com
          </a>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
            <span className="text-black font-bold text-sm">FX</span>
          </div>
          <div>
            <span className="text-foreground font-bold text-xl">United</span>
            <span className="text-gradient-gold font-bold text-xl">FXA</span>
          </div>
        </div>

        <div className="w-full max-w-md space-y-6 animate-slide-up">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Sign in to your account</h2>
            <p className="text-muted-foreground text-sm">Enter your credentials to access your trading portal</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Email address
              </label>
              <input
                type="email"
                placeholder="you@unitedfxa.com"
                className="w-full px-4 py-3 rounded-lg text-sm text-foreground placeholder-muted-foreground transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary"
                style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' },
                })}
              />
              {errors.email && (
                <p className="text-xs text-danger mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-11 rounded-lg text-sm text-foreground placeholder-muted-foreground transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
                  {...register('password', { required: 'Password is required' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-danger mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Root error */}
            {errors.root && (
              <div className="px-4 py-3 rounded-lg text-sm text-danger"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                {errors.root.message}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg font-semibold text-sm text-primary-foreground transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Don&apos;t have an account?{' '}
            <a href="https://www.unitedfxa.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
              Register on unitedfxa.com
            </a>
          </p>

          <p className="text-center text-xs text-muted-foreground">
            By signing in, you agree to our{' '}
            <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}